import { Helmet } from "react-helmet";
import type { ReactNode } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  twitterCard?: "summary" | "summary_large_image";
  noIndex?: boolean;
  children?: ReactNode;
}

export const SEOHead = ({
  title = "DIGIKA Freelance Hub – AI & Blockchain-Powered Marketplace",
  description = "DIGIKA is the AI-driven freelance hub that connects businesses and top talent worldwide. Powered by blockchain for secure payments, verified credentials, and intelligent job matching.",
  canonicalUrl,
  ogImage = "/og-image.png",
  ogType = "website",
  twitterCard = "summary_large_image",
  noIndex = false,
  children,
}: SEOProps) => {
  // Utilise une URL de base si fournie dans l'env, sinon window.origin
  const envSite = typeof import.meta !== "undefined" ? import.meta.env.VITE_SITE_URL : "";
  const siteUrl =
    (envSite && /^https?:\/\//.test(envSite) ? envSite.replace(/\/+$/, "") : "") ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const toAbs = (u?: string) =>
    !u ? undefined : u.startsWith("http") ? u : `${siteUrl}${u.startsWith("/") ? "" : "/"}${u}`;

  const fullCanonicalUrl = toAbs(canonicalUrl);
  const fullOgImage = toAbs(ogImage) ?? "";

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:alt" content={title} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:site_name" content="DIGIKA Freelance Hub" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Indexing */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Icons / PWA */}
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="icon" href="/favicon.ico" />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
      <meta name="msapplication-TileColor" content="#2b5797" />
      <meta name="theme-color" content="#ffffff" />

      {children}
    </Helmet>
  );
};
